import {
  Box,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Tbody,
  useToast,
  Spacer,
  useDisclosure,
} from '@chakra-ui/react';
import { getIn, setIn } from 'formik';
import lodash from 'lodash';
import React, { useState, useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { recurringDate } from '@finley-technologies/finley-date';
import {
  typeguards as finleyTypeguards,
  Util,
} from '@finley-technologies/finley-types';

import { DeleteArrayPropertyButton } from '../../delete/DeleteArrayPropertyButton';
import {
  EditGlobalRecurringDateModal,
  RecurringDateSetupType,
  SourceType,
} from '../EditRecurringDateModal';
import {
  RecurringDateConfigView,
  RelativeRecurringDateConfigView,
} from '../RecurringDateConfigView';
import {
  AddGlobalRecurringDateModal,
  createRecurringDateConfigShell,
} from './AddGlobalRecurringDateModal';
import { colors } from 'packages/common-frontend/colors';
import {
  EditIconButton,
  AddTextButton,
  CopyIconButton,
} from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const { cloneDeep } = lodash;

type GlobalRecurringDateConfig = CreditAgreement.Config.GlobalRecurringDate.Config;

type ReportingRecurringDateConfigFieldProps = {
  wipConfig?: WipConfig;
  recurringDateConfig: GlobalRecurringDateConfig;
  basePropertyKey: string;
  idx: number;
  preSelectedTriggerDate?: Util.FullDateStr;
};
export const ReportingRecurringDateConfigField = ({
  wipConfig,
  recurringDateConfig,
  basePropertyKey,
  idx,
  preSelectedTriggerDate,
}: ReportingRecurringDateConfigFieldProps) => {
  const toast = useToast();
  const {
    isOpen: isEditModalOpen,
    onClose: onEditModalClose,
    onOpen: onEditModalOpen,
  } = useDisclosure();

  const {
    isOpen: isDuplicateModalOpen,
    onOpen: onDuplicateModalOpen,
    onClose: onDuplicateModalClose,
  } = useDisclosure();

  const [
    isEditRecurringDateSaveLoading,
    setIsEditRecurringDateSaveLoading,
  ] = useState(false);
  const [
    isDuplicateRecurringDateSaveLoading,
    setIsDuplicateRecurringDateSaveLoading,
  ] = useState(false);

  const duplicateRecurringDateConfig = useCallback(
    async ({
      recurringDateFieldKeyInput,
    }: {
      recurringDateFieldKeyInput: string;
    }): Promise<void> => {
      if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
        return;
      }

      setIsDuplicateRecurringDateSaveLoading(true);

      const existingConfigs = getIn(
        wipConfig.formProps.values,
        basePropertyKey,
      );
      const copiedRecurringDateConfig = cloneDeep(recurringDateConfig);
      copiedRecurringDateConfig.field = recurringDateFieldKeyInput;

      const newConfigs = [...existingConfigs, copiedRecurringDateConfig];

      const updatedWipCreditAgreement = setIn(
        wipConfig.formProps.values,
        basePropertyKey,
        newConfigs,
      );

      const saveData = await wipConfig.saveCreditAgreementFullArtifactWip({
        variables: {
          creditAgreementFullArtifactWip: {
            id: wipConfig.formProps.values.id,
            creditFacilityId: wipConfig.formProps.values.creditFacilityId,
            creditAgreement: JSON.stringify(updatedWipCreditAgreement),
          },
        },
        notifyOnNetworkStatusChange: true,
      });

      if (saveData.data) {
        setIsDuplicateRecurringDateSaveLoading(false);
        onDuplicateModalClose();

        wipConfig.formProps.setFieldValue(basePropertyKey, newConfigs);
      } else {
        // if CA could not save, allow user to edit inside the modal and try again
        setIsDuplicateRecurringDateSaveLoading(false);
      }
    },
    [basePropertyKey, onDuplicateModalClose, wipConfig, recurringDateConfig],
  );

  const onSave = useCallback(
    async (
      newField: GlobalRecurringDateConfig,
      options?: { ignoreRecurringDateSetupValidate?: boolean },
    ): Promise<void> => {
      if (!wipConfig) {
        return;
      }

      setIsEditRecurringDateSaveLoading(true);

      const isValid = finleyTypeguards.creditAgreement.isGlobalRecurringDateConfig(
        newField,
      );
      if (!isValid) {
        toast({
          ...errorToastConfig,
          title: 'Errors',
          description:
            'Attempted to save invalid Recurring Date Config structure',
        });
        return;
      }

      const wipRecurringDateConfigCopy = cloneDeep(
        getIn(wipConfig.formProps.values, basePropertyKey),
      );
      finleyAssert(
        finleyTypeguards.creditAgreement.isGlobalRecurringDateConfigs(
          wipRecurringDateConfigCopy,
        ),
        `Retrieve non Recurring Date Config type for ${basePropertyKey}`,
      );

      const updatedRecurringDates = wipRecurringDateConfigCopy.map(
        (curConfig, loopIdx) =>
          idx === loopIdx
            ? { ...newField, field: newField.field.trim() }
            : curConfig,
      );

      const recurringDateValidate = recurringDate.validateGlobalRecurringDateConfigs(
        { globalRecurringDateConfigs: updatedRecurringDates, options },
      );
      if (recurringDateValidate.errors.length > 0) {
        toast({
          title: 'Global Recurring Date Error',
          description: recurringDateValidate.errors.join(', '),
          status: 'error',
          position: 'top',
          isClosable: true,
        });
        return;
      }

      const updatedWipCreditAgreement = setIn(
        wipConfig.formProps.values,
        basePropertyKey,
        updatedRecurringDates,
      );

      const saveData = await wipConfig.saveCreditAgreementFullArtifactWip({
        variables: {
          creditAgreementFullArtifactWip: {
            id: wipConfig.formProps.values.id,
            creditFacilityId: wipConfig.formProps.values.creditFacilityId,
            creditAgreement: JSON.stringify(updatedWipCreditAgreement),
          },
        },
        notifyOnNetworkStatusChange: true,
      });

      if (saveData.data) {
        setIsEditRecurringDateSaveLoading(false);

        wipConfig.formProps.setFieldValue(
          basePropertyKey,
          updatedRecurringDates,
        );

        onEditModalClose();
      } else {
        // if CA could not save, allow user to edit inside the modal and try again
        setIsEditRecurringDateSaveLoading(false);
      }
    },
    [wipConfig, basePropertyKey, toast, idx, onEditModalClose],
  );

  const { field, maxCount, defaultTo } = recurringDateConfig;

  return (
    <>
      <Tr>
        <Td>{field}</Td>
        <Td>
          {recurringDateConfig.setup.type === 'static' && (
            <RecurringDateConfigView
              recurringDateConfig={recurringDateConfig.setup.config}
              wipConfig={wipConfig}
              fullPropertyKey={`${basePropertyKey}.setup.config`}
              displayName="Recurring Dates"
              displayStyle="humanReadableString"
              enableEdit={false}
            />
          )}
          {recurringDateConfig.setup.type === 'relative' && (
            <RelativeRecurringDateConfigView
              relativeConfig={recurringDateConfig.setup.config}
              wipConfig={wipConfig}
              fullPropertyKey={`${basePropertyKey}.setup.config`}
              displayName="Recurring Dates"
              displayStyle="humanReadableString"
              enableEdit={false}
            />
          )}
        </Td>
        <Td>{maxCount}</Td>
        <Td>{defaultTo.value === '' ? '-' : defaultTo.value}</Td>
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <Td>
            <CopyIconButton
              ariaLabel="duplicate recurring dates configuration"
              label="Duplicate Recurring Dates Configuration"
              onClick={() => onDuplicateModalOpen()}
            />
            <AddGlobalRecurringDateModal
              isOpen={isDuplicateModalOpen}
              onClose={onDuplicateModalClose}
              onClickAdd={({ recurringDateFieldKeyInput }) =>
                duplicateRecurringDateConfig({
                  recurringDateFieldKeyInput,
                })
              }
              initialRecurringDateFieldKey={`${recurringDateConfig.field}Copy`}
              initialSetupType={recurringDateConfig.setup.type}
              isAddLoading={isDuplicateRecurringDateSaveLoading}
              mode="duplicate"
            />
            <EditIconButton
              label="Edit Recurring Date Configuration"
              ariaLabel="edit recurring date configuration"
              onClick={onEditModalOpen}
            />
            <EditGlobalRecurringDateModal
              onClose={onEditModalClose}
              isOpen={isEditModalOpen}
              onSave={onSave}
              globalRecurringDateConfig={recurringDateConfig}
              isSaveLoading={isEditRecurringDateSaveLoading}
              preSelectedTriggerDate={preSelectedTriggerDate}
            />
            <DeleteArrayPropertyButton
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              idx={idx}
              useConfirmationModal
            />
          </Td>
        )}
      </Tr>
    </>
  );
};

type ReportingGlobalRecurringDateConfigsProps = {
  globalRecurringDateConfigs: GlobalRecurringDateConfig[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
  source: SourceType;
  preSelectedTriggerDate?: Util.FullDateStr;
};
export const ReportingGlobalRecurringDateConfigs = ({
  wipConfig,
  basePropertyKey,
  globalRecurringDateConfigs,
  source,
  preSelectedTriggerDate,
}: ReportingGlobalRecurringDateConfigsProps) => {
  const {
    isOpen: isAddModalOpen,
    onOpen: onAddModalOpen,
    onClose: onAddModalClose,
  } = useDisclosure();

  const [isAddConfigLoading, setIsAddConfigLoading] = useState(false);

  const addRecurringDateConfig = useCallback(
    async ({
      recurringDateFieldKeyInput,
      setupTypeInput,
    }: {
      recurringDateFieldKeyInput: string;
      setupTypeInput: RecurringDateSetupType;
    }): Promise<void> => {
      if (!wipConfig?.isMutateAllowed(basePropertyKey)) {
        return;
      }

      setIsAddConfigLoading(true);

      const currentConfigs = getIn(wipConfig.formProps.values, basePropertyKey);
      const newConfigs = cloneDeep(currentConfigs);

      const recurringDateConfigDefault = createRecurringDateConfigShell({
        recurringDateFieldKey: recurringDateFieldKeyInput,
        setupType: setupTypeInput,
        source,
      });
      newConfigs.push(recurringDateConfigDefault);

      const updatedWipCreditAgreement = setIn(
        wipConfig.formProps.values,
        basePropertyKey,
        newConfigs,
      );

      const saveData = await wipConfig.saveCreditAgreementFullArtifactWip({
        variables: {
          creditAgreementFullArtifactWip: {
            id: wipConfig.formProps.values.id,
            creditFacilityId: wipConfig.formProps.values.creditFacilityId,
            creditAgreement: JSON.stringify(updatedWipCreditAgreement),
          },
        },
        notifyOnNetworkStatusChange: true,
      });

      if (saveData.data) {
        setIsAddConfigLoading(false);
        onAddModalClose();

        wipConfig.formProps.setFieldValue(basePropertyKey, newConfigs);
      } else {
        // if CA could not save, allow user to edit inside the modal and try again
        setIsAddConfigLoading(false);
      }
    },
    [basePropertyKey, onAddModalClose, source, wipConfig],
  );

  return (
    <Box id="reporting-global-recurring-date-configs-container" mb="6">
      <Text fontWeight="bold" fontSize="large" mr="2" mb="16px">
        Recurring Dates
      </Text>
      <Flex alignItems="center" mb="32px">
        <Text color={colors.black[70]}>
          Add the recurring date configurations to be utilized.
        </Text>
        <Spacer />
        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <>
            <AddTextButton
              ariaLabel="add recurring date"
              label="Add Recurring Date"
              onClick={onAddModalOpen}
            />
            <AddGlobalRecurringDateModal
              isOpen={isAddModalOpen}
              onClose={onAddModalClose}
              onClickAdd={({ setupTypeInput, recurringDateFieldKeyInput }) =>
                addRecurringDateConfig({
                  setupTypeInput,
                  recurringDateFieldKeyInput,
                })
              }
              initialRecurringDateFieldKey="recurringDateConfigurationKey"
              initialSetupType="static"
              isAddLoading={isAddConfigLoading}
              mode="add"
            />
          </>
        )}
      </Flex>

      {globalRecurringDateConfigs.length > 0 ? (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              <Th w="25%">Key</Th>
              <Th w="40%">Recurring Date</Th>
              <Th>Max Count</Th>
              <Th>Default Value</Th>
              {wipConfig?.isMutateAllowed(basePropertyKey) && <Th w="15%" />}
            </Tr>
          </Thead>
          <Tbody>
            {globalRecurringDateConfigs.map((recurringDateConfig, idx) => (
              <ReportingRecurringDateConfigField
                key={recurringDateConfig.field}
                wipConfig={wipConfig}
                recurringDateConfig={recurringDateConfig}
                basePropertyKey={basePropertyKey}
                idx={idx}
                preSelectedTriggerDate={preSelectedTriggerDate}
              />
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text p="4" border="1px" borderColor="gray.700">
          There are currently no recurring date configs set up.
        </Text>
      )}
    </Box>
  );
};
